import React, { useState, useEffect } from 'react';
import PhoneInput, { isValidPhone } from '@/components/common/PhoneInput';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useReferral } from '@/hooks/useReferral';
import { useFormToken } from '@/hooks/useFormToken';
import {
  ArrowRight, Calendar, MapPin, Clock, CheckCircle2, UserCheck,
  Wine, Ticket, Loader2,
} from 'lucide-react';
import { BACKEND_URL } from '@/config/api';
import { useSiteLang, dateLocale } from '@/components/site/SiteLang';

const API = BACKEND_URL;

/*
  /eventos/:slug — Landing dinámica por evento.

  Fuente de datos: GET /api/events/:slug (todo en JSON, sin binarios)
  La imagen del flyer se sirve aparte vía /api/events/:slug/flyer

  Registro: POST /api/events/:slug/register (NO usa /api/leads → soluciona el bug
  del "ya estás registrado" cuando una persona quiere registrarse a varios eventos).

  Sin datos de pago en la web pública: tras la reserva, el equipo escribe con los
  detalles. Los eventos ya celebrados no se muestran (el backend responde 404 con
  PAST_EVENT_DETAIL y aquí se enseña "Este evento ya se celebró").
*/

const PAST_EVENT_DETAIL = 'Este evento ya se celebró';

// Un evento se considera celebrado cuando su día ya ha pasado (hoy sigue visible).
const isPastEvent = (iso) => {
  if (!iso) return false;
  const d = new Date(iso);
  if (isNaN(d.getTime())) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const day = new Date(d);
  day.setHours(0, 0, 0, 0);
  return day < today;
};

// Errores locales de carga (se traducen al pintar); PAST_EVENT_DETAIL es el texto que envía el backend
const NOT_FOUND = '__not_found__';
const LOAD_ERROR = '__load_error__';

const T = {
  es: {
    started: 'El evento ya ha comenzado',
    cd: { days: 'Días', hours: 'Horas', min: 'Min', sec: 'Seg' },
    pastTitle: 'Este evento ya se celebró',
    pastText: 'Consulta los próximos encuentros del club.',
    seeUpcoming: 'Ver próximos eventos',
    notFound: 'Evento no encontrado',
    loadError: 'No se pudo cargar el evento',
    unavailable: 'Evento no disponible',
    seeAll: 'Ver todos los eventos',
    noFlyer: 'Sin flyer',
    time: (h) => `${h}h`,
    arrival: (h) => `Llegada ${h}h`,
    start: (h) => `Inicio ${h}h`,
    limited: 'Plazas limitadas',
    startsIn: 'Comienza en:',
    bookMyPlace: 'Reservar mi plaza',
    date: 'Fecha',
    location: 'Ubicación',
    access: 'Acceso',
    preRegistration: 'Inscripción previa',
    eyebrow: 'Reserva',
    formTitle: 'Reserva tu plaza',
    formIntro: 'Te confirmaremos los detalles de acceso por email.',
    invitedBy: 'Has sido invitado por',
    fullName: 'Nombre completo *',
    namePh: 'Tu nombre y apellidos',
    emailLabel: 'Email *',
    emailPh: 'tu@email.com',
    yourPlace: 'Tu plaza',
    ticketEvent: 'Evento',
    ticketEventDesc: 'Formación + networking',
    free: 'Sin coste',
    ticketCocktail: 'Evento + Cóctel',
    ticketCocktailDesc: 'Formación + networking + cóctel',
    acceptA: 'He leído y acepto la',
    privacy: 'política de privacidad',
    sending: 'Enviando...',
    confirm: 'Confirmar reserva',
    received: 'Reserva recibida',
    placeCocktail: (price) => `Plaza: Evento + Cóctel${price ? ` — ${price}` : ''}.`,
    placeEvent: 'Plaza: Evento.',
    next: 'El equipo te escribirá con los detalles.',
    errPolicy: 'Debes aceptar la política de privacidad',
    errPhone: 'Revisa el número de WhatsApp',
    errNetwork: 'Error al enviar la solicitud',
    errForm: 'Revisa los datos del formulario',
  },
  en: {
    started: 'The event has started',
    cd: { days: 'Days', hours: 'Hours', min: 'Min', sec: 'Sec' },
    pastTitle: 'This event has already taken place',
    pastText: 'Take a look at the club’s upcoming gatherings.',
    seeUpcoming: 'See upcoming events',
    notFound: 'Event not found',
    loadError: 'We couldn’t load this event',
    unavailable: 'Event unavailable',
    seeAll: 'See all events',
    noFlyer: 'No flyer',
    time: (h) => h,
    arrival: (h) => `Arrival ${h}`,
    start: (h) => `Start ${h}`,
    limited: 'Limited places',
    startsIn: 'Starts in:',
    bookMyPlace: 'Book my place',
    date: 'Date',
    location: 'Venue',
    access: 'Entry',
    preRegistration: 'Booking required',
    eyebrow: 'Booking',
    formTitle: 'Book your place',
    formIntro: 'We’ll confirm the entry details by email.',
    invitedBy: 'You’ve been invited by',
    fullName: 'Full name *',
    namePh: 'Your first name and surname',
    emailLabel: 'Email *',
    emailPh: 'you@email.com',
    yourPlace: 'Your place',
    ticketEvent: 'Event',
    ticketEventDesc: 'Training + networking',
    free: 'Free',
    ticketCocktail: 'Event + Cocktails',
    ticketCocktailDesc: 'Training + networking + cocktails',
    acceptA: 'I have read and accept the',
    privacy: 'privacy policy',
    sending: 'Sending…',
    confirm: 'Confirm booking',
    received: 'Booking received',
    placeCocktail: (price) => `Your place: Event + Cocktails${price ? ` — ${price}` : ''}.`,
    placeEvent: 'Your place: Event.',
    next: 'The team will be in touch with the details.',
    errPolicy: 'Please accept the privacy policy',
    errPhone: 'Please check your WhatsApp number',
    errNetwork: 'We couldn’t send your request',
    errForm: 'Please check the details in the form',
  },
};

const formatEur = (n, lang) => (typeof n === 'number' && n > 0 ? (lang === 'en' ? `€${n}` : `${n} €`) : null);

const CountdownTimer = ({ targetDate, startTime }) => {
  const { lang } = useSiteLang();
  const t = T[lang] || T.es;
  const [timeLeft, setTimeLeft] = useState(null);

  useEffect(() => {
    if (!targetDate) return;
    let dateStr = targetDate;
    if (startTime && !dateStr.includes('T')) {
      dateStr = `${dateStr.split('T')[0]}T${startTime}:00`;
    }
    const target = new Date(dateStr);
    if (isNaN(target.getTime())) return;

    const tick = () => {
      const diff = target - new Date();
      if (diff <= 0) {
        setTimeLeft({ days: 0, hours: 0, minutes: 0, seconds: 0, ended: true });
        return;
      }
      setTimeLeft({
        days: Math.floor(diff / (1000 * 60 * 60 * 24)),
        hours: Math.floor((diff / (1000 * 60 * 60)) % 24),
        minutes: Math.floor((diff / (1000 * 60)) % 60),
        seconds: Math.floor((diff / 1000) % 60),
        ended: false,
      });
    };
    tick();
    const t = setInterval(tick, 1000);
    return () => clearInterval(t);
  }, [targetDate, startTime]);

  if (!timeLeft) return null;
  if (timeLeft.ended) return (
    <p className="font-body text-sm text-[#D4AF37]">{t.started}</p>
  );

  return (
    <div className="grid grid-cols-4 gap-2 sm:gap-3 md:gap-4 max-w-md">
      {[
        { value: timeLeft.days, label: t.cd.days },
        { value: timeLeft.hours, label: t.cd.hours },
        { value: timeLeft.minutes, label: t.cd.min },
        { value: timeLeft.seconds, label: t.cd.sec },
      ].map((item, idx) => (
        <div key={idx} className="bg-[#0F172A] border border-white/5 px-2 py-3 sm:px-4 sm:py-4 text-center">
          <div className="font-heading text-2xl sm:text-3xl md:text-4xl font-bold leading-none text-[#D4AF37] tabular-nums">
            {String(item.value).padStart(2, '0')}
          </div>
          <div className="font-body text-[9px] sm:text-[10px] uppercase tracking-widest text-slate-400 mt-1">
            {item.label}
          </div>
        </div>
      ))}
    </div>
  );
};

const formatLongDate = (iso, lang) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return new Intl.DateTimeFormat(dateLocale(lang), {
      weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
    }).format(d);
  } catch { return iso; }
};

export default function EventoDetail() {
  const { slug } = useParams();
  const { lang } = useSiteLang();
  const t = T[lang] || T.es;
  const [event, setEvent] = useState(null);
  const [loadingEvent, setLoadingEvent] = useState(true);
  const [eventError, setEventError] = useState('');

  const [form, setForm] = useState({ nombre: '', email: '', whatsapp: '' });
  const formToken = useFormToken();  // defensa frente a bots (backend/antibot.py)
  const [ticket, setTicket] = useState('libre');
  const [acceptPolicy, setAcceptPolicy] = useState(false);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(null);
  const { referralCode, affiliateName } = useReferral();

  // Fetch event from API
  useEffect(() => {
    let cancelled = false;
    setLoadingEvent(true);
    axios.get(`${API}/api/events/${slug}`)
      .then((res) => { if (!cancelled) setEvent(res.data); })
      .catch((err) => {
        if (cancelled) return;
        const detail = err.response?.data?.detail;
        if (err.response?.status === 404) {
          setEventError(detail === PAST_EVENT_DETAIL ? PAST_EVENT_DETAIL : NOT_FOUND);
        } else {
          setEventError(LOAD_ERROR);
        }
      })
      .finally(() => { if (!cancelled) setLoadingEvent(false); });
    return () => { cancelled = true; };
  }, [slug]);

  const cocktailEnabled = Boolean(event?.cocktail_enabled);
  const cocktailPrice = formatEur(event?.cocktail_price_eur, lang);

  const scrollToForm = () => document.getElementById('registro')?.scrollIntoView({ behavior: 'smooth' });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!acceptPolicy) {
      toast.error(t.errPolicy);
      return;
    }
    if (!isValidPhone(form.whatsapp)) {
      toast.error(t.errPhone);
      return;
    }
    setLoading(true);
    try {
      const token = await formToken();
      await axios.post(`${API}/api/events/${slug}/register`, {
        form_token: token || undefined,
        name: form.nombre,
        email: form.email,
        whatsapp: form.whatsapp,
        ticket_type: cocktailEnabled ? ticket : 'libre',
        ...(referralCode ? { referral_code: referralCode } : {}),
      });
      setSubmitted({ ticket: cocktailEnabled ? ticket : 'libre', nombre: form.nombre });
      window.scrollTo({ top: document.getElementById('registro').offsetTop - 80, behavior: 'smooth' });
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(!err.response ? t.errNetwork : (typeof d === 'string' ? d : t.errForm));
    } finally {
      setLoading(false);
    }
  };

  // ── Loading / error states ─────────────────────────────────────────────
  if (loadingEvent) return (
    <div className="min-h-screen bg-[#0a1628] flex items-center justify-center pt-20">
      <Loader2 className="animate-spin text-[#D4AF37]" size={32} />
    </div>
  );
  if (eventError === PAST_EVENT_DETAIL || (event && isPastEvent(event.event_date))) return (
    <div className="min-h-screen bg-[#0a1628] flex flex-col items-center justify-center text-white p-8 text-center" data-testid="event-past">
      <h1 className="font-heading text-2xl sm:text-3xl mb-3">{t.pastTitle}</h1>
      <p className="font-body text-white/60 mb-6 max-w-md">{t.pastText}</p>
      <Link to="/eventos" className="inline-flex items-center gap-2 bg-[#D4AF37] text-[#0F172A] font-semibold px-6 h-12">
        {t.seeUpcoming} <ArrowRight className="w-4 h-4" />
      </Link>
    </div>
  );
  if (eventError || !event) return (
    <div className="min-h-screen bg-[#0a1628] flex flex-col items-center justify-center text-white p-8 text-center">
      <p className="text-lg mb-4">{eventError === NOT_FOUND ? t.notFound : eventError === LOAD_ERROR ? t.loadError : (eventError || t.unavailable)}</p>
      <Link to="/eventos" className="text-[#D4AF37] hover:underline">{t.seeAll}</Link>
    </div>
  );

  const flyerSrc = event.flyer_url ? `${API}${event.flyer_url}` : null;
  const longDate = formatLongDate(event.event_date, lang);

  return (
    <div className="min-h-screen bg-white pt-20">

      {/* Hero */}
      <section className="relative flex items-center pt-8 pb-12 lg:pt-12 lg:pb-16 lg:min-h-[80vh] bg-[#0a1628]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-16 items-center">
            {/* Flyer */}
            <div className="flex justify-center lg:justify-end order-1 lg:order-2">
              <div className="relative max-w-xs sm:max-w-sm lg:max-w-md w-full">
                <div className="absolute -inset-1 bg-[#D4AF37]/20 blur-xl" />
                {flyerSrc ? (
                  <img src={flyerSrc} alt={event.title} className="relative w-full h-auto shadow-2xl" />
                ) : (
                  <div className="relative w-full aspect-[3/4] bg-slate-800 flex items-center justify-center text-white/40 shadow-2xl">
                    {t.noFlyer}
                  </div>
                )}
              </div>
            </div>

            {/* Texto */}
            <div className="order-2 lg:order-1">
              <div className="inline-flex items-center gap-2 bg-[#D4AF37]/15 border border-[#D4AF37]/40 px-3 py-1.5 sm:px-4 sm:py-2 mb-5 sm:mb-6">
                <Calendar className="w-4 h-4 text-[#D4AF37]" />
                <span className="font-body text-[11px] sm:text-xs text-[#D4AF37] uppercase tracking-widest">
                  {longDate}{event.start_time ? ` · ${t.time(event.start_time)}` : ''}
                </span>
              </div>

              <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-bold text-white leading-[1.15] mb-5 sm:mb-6 break-words">
                {event.title}
              </h1>

              {event.subtitle && (
                <p className="font-body text-base sm:text-lg text-white/75 mb-6 sm:mb-8 leading-relaxed">
                  {event.subtitle}
                </p>
              )}

              <div className="flex flex-wrap items-center gap-x-4 gap-y-2 mb-6 sm:mb-8">
                {(event.location_name || event.city) && (
                  <div className="flex items-center gap-2 text-white/70">
                    <MapPin className="w-4 h-4 sm:w-5 sm:h-5 text-[#D4AF37] flex-shrink-0" />
                    <span className="font-body text-sm">{[event.location_name, event.city].filter(Boolean).join(' · ')}</span>
                  </div>
                )}
                {(event.arrival_time || event.start_time) && (
                  <div className="flex items-center gap-2 text-white/70">
                    <Clock className="w-4 h-4 sm:w-5 sm:h-5 text-[#D4AF37] flex-shrink-0" />
                    <span className="font-body text-sm">
                      {event.arrival_time && t.arrival(event.arrival_time)}
                      {event.arrival_time && event.start_time && ' · '}
                      {event.start_time && t.start(event.start_time)}
                    </span>
                  </div>
                )}
                <div className="flex items-center gap-2 text-white/70">
                  <Ticket className="w-4 h-4 sm:w-5 sm:h-5 text-[#D4AF37] flex-shrink-0" />
                  <span className="font-body text-sm">{t.limited}</span>
                </div>
              </div>

              <div className="mb-6 sm:mb-8">
                <p className="font-body text-[11px] sm:text-xs text-white/50 uppercase tracking-widest mb-2 sm:mb-3">{t.startsIn}</p>
                <CountdownTimer targetDate={event.event_date} startTime={event.start_time} />
              </div>

              <Button
                onClick={scrollToForm}
                className="w-full sm:w-auto h-12 sm:h-14 px-6 sm:px-8 bg-[#D4AF37] text-[#0F172A] hover:bg-[#C5A028] font-bold uppercase tracking-wider rounded-none inline-flex items-center justify-center gap-2 text-sm sm:text-base"
              >
                {t.bookMyPlace} <ArrowRight className="w-5 h-5" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Detalles */}
      <section className="py-14 sm:py-20 md:py-24 bg-[#F8FAFC]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6">
            <div className="bg-white p-6 sm:p-8 border border-slate-100 text-center">
              <Calendar className="w-8 h-8 text-[#D4AF37] mx-auto mb-4" />
              <h3 className="font-heading text-lg text-[#0F172A] mb-2">{t.date}</h3>
              <p className="font-body text-slate-600 capitalize">{longDate}</p>
              {(event.arrival_time || event.start_time) && (
                <p className="font-body text-sm text-slate-400 mt-1">
                  {event.arrival_time && t.arrival(event.arrival_time)}
                  {event.arrival_time && event.start_time && ' · '}
                  {event.start_time && t.start(event.start_time)}
                </p>
              )}
            </div>
            <div className="bg-white p-6 sm:p-8 border border-slate-100 text-center">
              <MapPin className="w-8 h-8 text-[#D4AF37] mx-auto mb-4" />
              <h3 className="font-heading text-lg text-[#0F172A] mb-2">{t.location}</h3>
              <p className="font-body text-slate-600">{event.location_name || '—'}</p>
              {event.location_address && (
                <p className="font-body text-sm text-slate-400 mt-1">{event.location_address}</p>
              )}
            </div>
            <div className="bg-white p-6 sm:p-8 border border-slate-100 text-center">
              <Ticket className="w-8 h-8 text-[#D4AF37] mx-auto mb-4" />
              <h3 className="font-heading text-lg text-[#0F172A] mb-2">{t.access}</h3>
              <p className="font-body text-slate-600">{t.preRegistration}</p>
              <p className="font-body text-sm text-slate-400 mt-1">{t.limited}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Formulario */}
      <section id="registro" className="py-14 sm:py-20 md:py-28 bg-[#0F172A]">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8 sm:mb-10">
            <span className="font-body text-xs uppercase tracking-widest text-[#D4AF37] mb-3 sm:mb-4 block">{t.eyebrow}</span>
            <h2 className="font-heading text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-3 sm:mb-4 leading-tight">{t.formTitle}</h2>
            <p className="font-body text-sm sm:text-base text-white/60">{t.formIntro}</p>
          </div>

          {!submitted ? (
            <form onSubmit={handleSubmit} className="bg-white/5 border border-white/10 p-5 sm:p-8 space-y-4 sm:space-y-5">
              {affiliateName && (
                <div className="flex items-center gap-3 bg-[#D4AF37]/10 border border-[#D4AF37]/30 px-4 py-3">
                  <UserCheck className="w-5 h-5 text-[#D4AF37] flex-shrink-0" />
                  <p className="font-body text-sm text-white">
                    {t.invitedBy} <strong className="text-[#D4AF37]">{affiliateName}</strong>
                  </p>
                </div>
              )}

              <div>
                <label className="font-body text-xs text-white/50 uppercase tracking-wider mb-1 block">{t.fullName}</label>
                <Input value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })}
                  className="h-12 rounded-none bg-white/5 border-white/10 text-white placeholder:text-white/30 focus:border-[#D4AF37]"
                  placeholder={t.namePh} required minLength={2} />
              </div>
              <div>
                <label className="font-body text-xs text-white/50 uppercase tracking-wider mb-1 block">{t.emailLabel}</label>
                <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="h-12 rounded-none bg-white/5 border-white/10 text-white placeholder:text-white/30 focus:border-[#D4AF37]"
                  placeholder={t.emailPh} required />
              </div>
              <div>
                <label className="font-body text-xs text-white/50 uppercase tracking-wider mb-1 block">WhatsApp *</label>
                <PhoneInput value={form.whatsapp} onChange={(v) => setForm({ ...form, whatsapp: v })} variant="dark" required testId="event-whatsapp" ariaLabel="WhatsApp"  lang={lang} />
              </div>

              {/* Plaza: acceso al evento y, solo si el evento lo ofrece, opción con cóctel */}
              <div>
                <label className="font-body text-xs text-white/50 uppercase tracking-wider mb-3 block">{t.yourPlace}</label>
                <div className="space-y-2">
                  <button type="button" onClick={() => setTicket('libre')} data-testid="ticket-libre"
                    className={`w-full text-left p-3 sm:p-4 border-2 transition-colors ${
                      !cocktailEnabled || ticket === 'libre' ? 'border-[#D4AF37] bg-[#D4AF37]/10' : 'border-white/10 hover:border-white/20'
                    }`}>
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-2 sm:gap-3 min-w-0">
                        <Ticket className="w-5 h-5 flex-shrink-0 text-[#D4AF37]" />
                        <div className="min-w-0">
                          <p className="font-body text-sm font-semibold text-white">{t.ticketEvent}</p>
                          <p className="font-body text-[11px] sm:text-xs text-white/50">{t.ticketEventDesc}</p>
                        </div>
                      </div>
                      <span className="font-body text-sm text-white/70 flex-shrink-0">{t.free}</span>
                    </div>
                  </button>
                  {cocktailEnabled && (
                    <button type="button" onClick={() => setTicket('cocktail')} data-testid="ticket-cocktail"
                      className={`w-full text-left p-3 sm:p-4 border-2 transition-colors ${
                        ticket === 'cocktail' ? 'border-[#D4AF37] bg-[#D4AF37]/10' : 'border-white/10 hover:border-white/20'
                      }`}>
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex items-center gap-2 sm:gap-3 min-w-0">
                          <Wine className="w-5 h-5 flex-shrink-0 text-[#D4AF37]" />
                          <div className="min-w-0">
                            <p className="font-body text-sm font-semibold text-white">{t.ticketCocktail}</p>
                            <p className="font-body text-[11px] sm:text-xs text-white/50">{t.ticketCocktailDesc}</p>
                          </div>
                        </div>
                        {cocktailPrice && (
                          <span className="font-heading text-lg sm:text-xl text-[#D4AF37] flex-shrink-0">{cocktailPrice}</span>
                        )}
                      </div>
                    </button>
                  )}
                </div>
              </div>

              <label className="flex items-start gap-3 cursor-pointer pt-2">
                <input type="checkbox" checked={acceptPolicy} onChange={(e) => setAcceptPolicy(e.target.checked)}
                  className="mt-0.5 h-5 w-5 rounded border-white/20 bg-white/5 text-[#D4AF37] focus:ring-[#D4AF37]" />
                <span className="font-body text-sm text-white/60 leading-relaxed">
                  {t.acceptA}{' '}
                  <a href="/privacy-policy-fhc" target="_blank" rel="noopener noreferrer" className="text-[#D4AF37] underline hover:text-[#C5A028]">
                    {t.privacy}
                  </a>
                </span>
              </label>

              <Button type="submit" disabled={loading || !acceptPolicy}
                className="w-full h-14 bg-[#D4AF37] text-[#0F172A] hover:bg-[#C5A028] font-bold uppercase tracking-wider rounded-none inline-flex items-center justify-center gap-2 disabled:opacity-40">
                {loading ? (
                  <span className="inline-flex items-center gap-2"><Loader2 className="w-5 h-5 animate-spin" /> {t.sending}</span>
                ) : (
                  <span className="inline-flex items-center gap-2">{t.confirm} <ArrowRight className="w-5 h-5" /></span>
                )}
              </Button>
            </form>
          ) : (
            <div className="bg-white/5 border border-[#D4AF37]/30 p-6 sm:p-10 text-center">
              <div className="w-16 h-16 sm:w-20 sm:h-20 mx-auto bg-[#0F172A] rounded-full flex items-center justify-center mb-5 sm:mb-6 border border-[#D4AF37]/40">
                <CheckCircle2 className="w-8 h-8 sm:w-10 sm:h-10 text-[#D4AF37]" />
              </div>
              <h3 className="font-heading text-xl sm:text-2xl md:text-3xl text-white mb-3 leading-tight break-words">
                {t.received}{submitted.nombre ? `, ${submitted.nombre.split(' ')[0]}` : ''}
              </h3>

              <p className="font-body text-sm sm:text-base text-white/70 mt-2">
                {submitted.ticket === 'cocktail'
                  ? t.placeCocktail(cocktailPrice)
                  : t.placeEvent}
              </p>
              <p className="font-body text-sm sm:text-base text-white/70 mt-3" data-testid="event-registered-next">
                {t.next}
              </p>
            </div>
          )}
        </div>
      </section>

    </div>
  );
}
